import type { inferProcedureInput, inferProcedureOutput } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../utils/trpc'
import type { FeatureFlag, FeatureFlagFormInput, FeatureFlagRegistryEntryView } from './types'

type Router = AppRouter['admin']['featureFlagsManagementPage']
type InitialData = inferProcedureOutput<Router['search']>
export type SearchParams = inferProcedureInput<Router['search']>
export interface OrganizationWithFlagStatus {
	id: string
	name: string
	enabled: boolean
}

// ============================================================================
// NEW ZUSTAND STORE PATTERN (TL preferred approach)
// ============================================================================

interface FeatureFlagsManagementState {
	flags: InitialData
	selectedFlag: FeatureFlag | null
	organizations: OrganizationWithFlagStatus[]
	availableEntries: FeatureFlagRegistryEntryView[]
	availableEntriesError: string | null
	isLoading: boolean
	isMutating: boolean
	isLoadingAvailableEntries: boolean
	error: string | null
	pagination: {
		page: number
		perPage: number
		total: number
		totalInPage: number
	}
	searchParams: SearchParams
	searchTimeout: NodeJS.Timeout
	setSearchParams: (params?: SearchParams) => void

	initialize: () => Promise<void>
	search: () => Promise<void>
	fetchFlags: () => Promise<void>
	fetchFlagById: (id: string) => Promise<FeatureFlag | null>
	fetchOrganizations: (flagId: string) => Promise<void>
	fetchAvailableEntries: () => Promise<void>
	createFlag: (data: FeatureFlagFormInput) => Promise<FeatureFlag>
	updateFlag: (id: string, data: FeatureFlagFormInput) => Promise<void>
	deleteFlag: (id: string) => Promise<void>
	bulkToggle: (input: { flagId: string; organizationIds: string[]; enabled: boolean }) => Promise<void>
	setPage: (page: number) => void
	setPerPage: (perPage: number) => void
	setSearch: (search: string) => void
	setSort: (sortBy: string, order: 'asc' | 'desc') => void
	setSelectedFlag: (flag: FeatureFlag | null) => void
	clearError: () => void
	reset: () => void
}

const initialPagination = {
	page: 1,
	perPage: 25,
	total: 0,
	totalInPage: 0,
}

const initialSearchParams = {
	search: '',
	sortBy: 'name',
	order: 'asc' as const,
}

export const useFeatureFlagsManagementStore = create<FeatureFlagsManagementState>()(
	devtools(
		(set, get) => ({
			selectedFlag: null,
			organizations: [],
			availableEntries: [],
			availableEntriesError: null,
			isLoading: false,
			isMutating: false,
			isLoadingAvailableEntries: true,
			error: null,
			pagination: { ...initialPagination },
			searchParams: { ...initialSearchParams },

			initialize: async () => {
				const { search } = get()
				set({ isLoading: true, error: null })

				search()
			},

			search: async () => {
				const { searchParams } = get()

				const flags = await trpcClient.admin.featureFlagsManagementPage.search.query(searchParams)

				set({ flags })
			},

			setSearchParams: (params) => {
				const { searchTimeout, search } = get()

				if (searchTimeout) {
					clearTimeout(searchTimeout)
				}

				set(({ searchParams }) => ({
					searchParams: {
						...searchParams,
						...params,
					},
					searchTimeout: setTimeout(() => search(), 300),
				}))
			},

			fetchFlags: async () => {
				const { pagination, searchParams } = get()
				set({ isLoading: true, error: null })

				try {
					const result = await trpcClient.admin.featureFlagsManagementPage.search.query({
						page: pagination.page,
						perPage: pagination.perPage,
						search: searchParams.search,
						sortBy: searchParams.sortBy,
						order: searchParams.order,
					})

					set({
						flags: result,
						isLoading: false,
					})
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to fetch feature flags',
						isLoading: false,
					})
				}
			},

			fetchFlagById: async (id: string) => {
				set({ isLoading: true, error: null })

				try {
					const flag = await trpcClient.admin.featureFlagsManagementPage.getById.query({ id })
					set({ selectedFlag: flag as FeatureFlag, isLoading: false })
					return flag as FeatureFlag
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to fetch feature flag',
						isLoading: false,
					})
					return null
				}
			},

			fetchAvailableEntries: async () => {
				set({ isLoadingAvailableEntries: true, availableEntriesError: null })
				try {
					const entries = await trpcClient.admin.featureFlagsManagementPage.getAvailableKeys.query()
					set({ availableEntries: entries, isLoadingAvailableEntries: false })
				} catch (error) {
					const message = error instanceof Error ? error.message : 'Failed to fetch available feature flag keys'
					set({
						availableEntriesError: message,
						isLoadingAvailableEntries: false,
					})
				}
			},

			fetchOrganizations: async (flagId: string) => {
				set({ isLoading: true, error: null })

				try {
					const organizations = await trpcClient.admin.featureFlagsManagementPage.getOrganizations.query({ flagId })
					set({ organizations: organizations as OrganizationWithFlagStatus[], isLoading: false })
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to fetch organizations',
						isLoading: false,
					})
				}
			},

			createFlag: async (data: FeatureFlagFormInput) => {
				set({ isMutating: true, error: null })

				try {
					if (data.key === '') throw new Error('Feature flag key is required')
					const { key, ...rest } = data
					const result = await trpcClient.admin.featureFlagsManagementPage.create.mutate({ ...rest, key })
					set({ isMutating: false })
					await get().fetchFlags()
					await get().fetchAvailableEntries()
					return result
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to create feature flag',
						isMutating: false,
					})
					throw error
				}
			},

			updateFlag: async (id: string, data: FeatureFlagFormInput) => {
				set({ isMutating: true, error: null })

				try {
					await trpcClient.admin.featureFlagsManagementPage.update.mutate({ id, ...data })
					set({ isMutating: false })
					await get().fetchFlags()
					if (get().selectedFlag?.id === id) {
						await get().fetchFlagById(id)
					}
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to update feature flag',
						isMutating: false,
					})
					throw error
				}
			},

			deleteFlag: async (id: string) => {
				set({ isMutating: true, error: null })

				try {
					await trpcClient.admin.featureFlagsManagementPage.delete.mutate({ id })
					set({ isMutating: false, selectedFlag: null })
					await get().fetchFlags()
					// soft-deleted keys still occupy uniqueness, so refresh dropdown availability
					await get().fetchAvailableEntries()
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to delete feature flag',
						isMutating: false,
					})
					throw error
				}
			},

			bulkToggle: async (input: { flagId: string; organizationIds: string[]; enabled: boolean }) => {
				set({ isMutating: true, error: null })

				try {
					await trpcClient.admin.organizationFeatureFlagsTab.bulkToggle.mutate(input)
					set({ isMutating: false })
					await get().fetchFlags()
					await get().fetchOrganizations(input.flagId)
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to update feature flag organizations',
						isMutating: false,
					})
					throw error
				}
			},

			setPage: (page: number) => {
				set((state) => ({
					pagination: { ...state.pagination, page },
				}))
				get().fetchFlags()
			},

			setPerPage: (perPage: number) => {
				set((state) => ({
					pagination: { ...state.pagination, perPage, page: 1 },
				}))
				get().fetchFlags()
			},

			setSearch: (search: string) => {
				set((state) => ({
					searchParams: { ...state.searchParams, search },
					pagination: { ...state.pagination, page: 1 },
				}))
				get().fetchFlags()
			},

			setSort: (sortBy: string, order: 'asc' | 'desc') => {
				set((state) => ({
					searchParams: { ...state.searchParams, sortBy, order },
				}))
				get().fetchFlags()
			},

			setSelectedFlag: (flag: FeatureFlag | null) => {
				set({ selectedFlag: flag })
			},

			clearError: () => {
				set({ error: null })
			},
		}),
		{ name: 'feature-flags-management-store' },
	),
)

// Organization Feature Flags Store (for the organization detail page tab)
interface OrganizationFeatureFlagsState {
	flags: Array<{
		id: string
		key: string
		name: string
		description: string | null
		enabledByDefault: boolean
		enabled: boolean
	}>
	isLoading: boolean
	isMutating: boolean
	error: string | null
	isForbidden: boolean

	fetchFlags: (organizationId: string) => Promise<void>
	toggleFlag: (organizationId: string, flagKey: string, enabled: boolean) => Promise<void>
	clearError: () => void
	reset: () => void
}

export const useOrganizationFeatureFlagsStore = create<OrganizationFeatureFlagsState>()(
	devtools(
		(set, get) => ({
			flags: [],
			isLoading: false,
			isMutating: false,
			error: null,
			isForbidden: false,

			fetchFlags: async (organizationId: string) => {
				set({ isLoading: true, error: null, isForbidden: false })

				try {
					const flags = await trpcClient.admin.organizationFeatureFlagsTab.getForOrganization.query({
						organizationId,
					})
					set({ flags, isLoading: false })
				} catch (error) {
					const errorMessage = error instanceof Error ? error.message : 'Failed to fetch feature flags'
					const isForbidden =
						errorMessage.includes('engineering team') ||
						errorMessage.includes('Only engineering team') ||
						(error as { data?: { code?: string } })?.data?.code === 'FORBIDDEN'

					set({
						error: errorMessage,
						isLoading: false,
						isForbidden,
					})
				}
			},

			toggleFlag: async (organizationId: string, flagKey: string, enabled: boolean) => {
				set({ isMutating: true, error: null })

				try {
					await trpcClient.admin.organizationFeatureFlagsTab.toggle.mutate({
						flagKey,
						organizationId,
						enabled,
					})
					set({ isMutating: false })
					await get().fetchFlags(organizationId)
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to update feature flag',
						isMutating: false,
					})
					throw error
				}
			},

			clearError: () => {
				set({ error: null })
			},

			reset: () => {
				set({
					flags: [],
					isLoading: false,
					isMutating: false,
					error: null,
					isForbidden: false,
				})
			},
		}),
		{ name: 'organization-feature-flags-store' },
	),
)
